///
/// EarthBrowser
///
/// Created by Matt Giger on 12/24/2024
/// Copyright (c) 2024 Geodata Labs
///

import { identifyUser, clearUser } from "@/app/sentry"
import { clearTileToken } from "@/services/tiles/TileTokenService"
import type {
	PasskeyAuthenticationOptions,
	PasskeyAuthenticationRequest,
	PasskeyCredentialListResponse,
	PasskeyDeleteRequest,
	PasskeyRegistrationOptions,
	PasskeyRegistrationRequest,
	PasskeyRegistrationResponse,
	PasskeyRenameRequest,
	RegisterWithPasskeyCompleteRequest,
	RegisterWithPasskeyInitRequest
} from "@shared-types/passkey"
import type { Subscription } from "@shared-types/subscription"
import type {
	LinkedAccountRecord,
	NotificationSettings,
	PrivacySettingsResponse,
	UpdateNotificationSettingsRequest,
	UpdatePrivacySettingsRequest,
	UserAccountsResponse,
	UserRecordEntry,
} from "@shared-types/user"
import { track } from "@/utils/analytics"
import posthog from "posthog-js"
import { authenticatedApi } from "./API/authenticatedApi"
import { persistor, type RootState } from "./store"
import { setCookieSettings, setUserProfile } from "./userSlice"

// Guard so a freshly created account emits its terminal funnel event once
// per app load, not on every profile refetch
let capturedAccountCreated = false

export const userApi = authenticatedApi.injectEndpoints({
	endpoints: (builder) => ({
		signOutUser: builder.mutation<void, void>({
			query: () => ({
				url: "/api/user/signout",
				method: "GET",
			}),
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {

				try {
					await queryFulfilled

					clearTileToken()
					clearUser()
					// Unlink analytics from the account; the eb_anon_id super
					// property keeps device continuity for the funnel
					try { posthog.reset() } catch { /* best-effort */ }
					dispatch(setUserProfile(undefined))
					// Flush the persisted state to localStorage
					await persistor.flush()

					// Reset the API state
					dispatch(authenticatedApi.util.resetApiState())

				} catch {
				}
			},
		}),

		// Permanent self-service account deletion (store requirement). The
		// server cancels Stripe, removes stored files and cascades the row;
		// locally this is a sign-out with the same cleanup
		deleteAccount: builder.mutation<{ success: boolean }, void>({
			query: () => ({
				url: "/api/user/account",
				method: "DELETE",
			}),
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled
					clearTileToken()
					clearUser()
					try { posthog.reset() } catch { /* best-effort */ }
					dispatch(setUserProfile(undefined))
					await persistor.flush()
					dispatch(authenticatedApi.util.resetApiState())
				} catch {
				}
			},
		}),

		getUserProfile: builder.query<UserRecordEntry, void>({
			query: () => "/api/user/profile",
			providesTags: ["UserProfile"],
			async onQueryStarted(_arg, api) {
				const { dispatch, queryFulfilled } = api
				try {
					const wasSignedOut = !(api.getState() as unknown as RootState).user.userProfile
					const { data } = await queryFulfilled
					dispatch(setUserProfile(data))
					// Tile tokens carry the access tier: drop any anonymous
					// token minted before sign-in so the next tile request
					// mints one with this account's entitlements
					if (wasSignedOut) {
						clearTileToken()
					}
					identifyUser(String(data.id), data.email, data.username)
					// Stitch this device's anonymous event history to the
					// account (no-op while analytics consent is opted out)
					try {
						posthog.identify(String(data.id), { email: data.email })
						// A profile younger than 5 minutes means registration
						// just completed (OAuth registration finishes via
						// redirect, so there is no client-side success callback
						// to instrument directly)
						const accountAgeMs = Date.now() - new Date(data.created_at).getTime()
						if (!capturedAccountCreated && accountAgeMs < 5 * 60_000) {
							capturedAccountCreated = true
							track("account_created", {})
						}
					} catch { /* analytics is best-effort */ }
				} catch {
					dispatch(setUserProfile(undefined))
					clearUser()
				}
			},
		}),
		updateUserProfile: builder.mutation<{ success: boolean }, Partial<UserRecordEntry>>({
			query: (userProfile) => ({
				url: "/api/user/profile",
				method: "PUT",
				body: userProfile,
			}),
			invalidatesTags: ["UserProfile"],
		}),
		uploadAvatar: builder.mutation<{ image_url: string }, Blob>({
			query: (image) => ({
				url: "/api/user/avatar",
				method: "POST",
				headers: { "Content-Type": image.type },
				body: image,
			}),
			invalidatesTags: ["UserProfile"],
			extraOptions: { maxRetries: 0 },
		}),

		// Notification settings
		getNotificationSettings: builder.query<NotificationSettings, void>({
			query: () => "/api/user/notification-settings",
			providesTags: ["NotificationSettings"],
		}),
		updateNotificationSettings: builder.mutation<NotificationSettings, UpdateNotificationSettingsRequest>({
			query: (settings) => ({
				url: "/api/user/notification-settings",
				method: "PUT",
				body: settings,
			}),
			invalidatesTags: ["NotificationSettings"],
		}),

		// Passkey authentication
		getPasskeyRegistrationOptions: builder.mutation<PasskeyRegistrationOptions, void>({
			query: () => ({
				url: "/api/user/passkey/registration-options",
				method: "GET",
			}),
			// Ensure mutations never cache
			extraOptions: { maxRetries: 0 },
		}),
		registerPasskey: builder.mutation<PasskeyRegistrationResponse, PasskeyRegistrationRequest>({
			query: (request) => ({
				url: "/api/user/passkey/register",
				method: "POST",
				body: request,
			}),
			invalidatesTags: ["PasskeyCredentials"],
		}),
		getPasskeyAuthenticationOptions: builder.mutation<PasskeyAuthenticationOptions, void>({
			query: () => ({
				url: "/api/user/passkey/authentication-options",
				method: "GET",
			}),
			// Ensure mutations never cache
			extraOptions: { maxRetries: 0 },
		}),
		authenticateWithPasskey: builder.mutation<UserRecordEntry, PasskeyAuthenticationRequest>({
			query: (request) => ({
				url: "/api/user/passkey/authenticate",
				method: "POST",
				headers: {
					"X-Turnstile-Token": request.turnstileToken || "",
				},
				body: request,
			}),
			invalidatesTags: ["UserProfile"],
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled
					dispatch(setUserProfile(data))
				} catch {
					// Handle error if needed
				}
			},
		}),
		getPasskeyCredentials: builder.query<PasskeyCredentialListResponse, void>({
			query: () => "/api/user/passkey/credentials",
			providesTags: ["PasskeyCredentials"],
		}),
		deletePasskeyCredential: builder.mutation<{ success: boolean }, PasskeyDeleteRequest>({
			query: (request) => ({
				url: "/api/user/passkey/credential",
				method: "DELETE",
				body: request,
			}),
			invalidatesTags: ["PasskeyCredentials"],
		}),
		renamePasskeyCredential: builder.mutation<{ success: boolean }, PasskeyRenameRequest>({
			query: (request) => ({
				url: "/api/user/passkey/credential",
				method: "PUT",
				body: request,
			}),
			invalidatesTags: ["PasskeyCredentials"],
		}),

		// Register with passkey (account creation + passkey in one step)
		registerWithPasskeyInit: builder.mutation<PasskeyRegistrationOptions, RegisterWithPasskeyInitRequest>({
			query: (request) => ({
				url: "/api/user/register-with-passkey-init",
				method: "POST",
				headers: {
					"X-Turnstile-Token": request.turnstileToken,
				},
				body: {
					firstName: request.firstName,
					lastName: request.lastName,
					email: request.email,
					deviceFingerprint: request.deviceFingerprint,
				},
			}),
		}),
		registerWithPasskeyComplete: builder.mutation<{ success: boolean; message: string; email: string }, RegisterWithPasskeyCompleteRequest>({
			query: (request) => ({
				url: "/api/user/register-with-passkey-complete",
				method: "POST",
				body: request,
			}),
			// Don't invalidate tags since user is not signed in yet
		}),

		// Privacy settings
		getPrivacySettings: builder.query<PrivacySettingsResponse, void>({
			query: () => "/api/user/privacy-settings",
			providesTags: ["PrivacySettings"],
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled
					// Sync privacy settings to Redux (for backward compatibility with cookie consent)
					dispatch(setCookieSettings({
						performance: data.performance_cookies,
						personalization: data.personalization_cookies,
					}))
				} catch {
					// Handle error if needed
				}
			},
		}),
		updatePrivacySettings: builder.mutation<PrivacySettingsResponse, UpdatePrivacySettingsRequest>({
			query: (settings) => ({
				url: "/api/user/privacy-settings",
				method: "PUT",
				body: settings,
			}),
			invalidatesTags: ["PrivacySettings"],
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled
					// Sync privacy settings to Redux (for backward compatibility with cookie consent)
					dispatch(setCookieSettings({
						performance: data.performance_cookies,
						personalization: data.personalization_cookies,
					}))
				} catch {
					// Handle error if needed
				}
			},
		}),
	}),
})

export const userAccountsApi = authenticatedApi.injectEndpoints({

	endpoints: (builder) => ({
		getUserAccountInfo: builder.query<UserAccountsResponse, void>({
			query: () => "/api/user/account-info",
			providesTags: ["UserAccounts"],
		}),
		unlinkAccount: builder.mutation<{ success: boolean }, number>({
			query: (accountId) => ({
				url: `/api/user/accounts/${accountId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["UserAccounts"],
		}),
		revokeDevice: builder.mutation<{ success: boolean }, number>({
			query: (deviceId) => ({
				url: `/api/user/devices/${deviceId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["UserAccounts"],
		}),
		setPrimaryAccount: builder.mutation<LinkedAccountRecord, number>({
			query: (accountId) => ({
				url: `/api/user/accounts/${accountId}/set-primary`,
				method: "PUT",
			}),
			invalidatesTags: ["UserAccounts"],
		}),
		getUserSubscription: builder.query<Subscription[], void>({
			query: () => "/api/user/subscription",
			providesTags: ["UserSubscription"],
		}),
		getFullAccessOffer: builder.query<{ eligible: boolean, grantDays: number }, void>({
			query: () => "/api/user/full-access-offer",
			providesTags: ["FullAccessOffer"],
		}),
		acceptFullAccessOffer: builder.mutation<{ granted: boolean, expiresAt: string }, void>({
			query: () => ({
				url: "/api/user/full-access-offer/accept",
				method: "POST",
			}),
			invalidatesTags: ["FullAccessOffer", "UserSubscription"],
		}),
		// Founding-supporter offer (email-only; queried solely when the
		// ?offer=supporter deep link opens the redemption card)
		getSupporterOffer: builder.query<{
			available: boolean
			expiresAt: string | null
			price: string | null
			regularPrice: string | null
		}, void>({
			query: () => "/api/user/supporter-offer",
		}),
		createCheckoutSession: builder.mutation<{ url: string }, { offer?: "supporter" } | void>({
			query: (body) => ({
				url: "/api/stripe/create-checkout-session",
				method: "POST",
				...(body?.offer ? { body: { offer: body.offer } } : {}),
			}),
			extraOptions: { maxRetries: 0 },
		}),
		createPortalSession: builder.mutation<{ url: string }, void>({
			query: () => ({
				url: "/api/stripe/create-portal-session",
				method: "POST",
			}),
			extraOptions: { maxRetries: 0 },
		}),
	}),
})

export const {
	useSignOutUserMutation,
	useDeleteAccountMutation,
	useGetUserProfileQuery,
	useUpdateUserProfileMutation,
	useGetPasskeyRegistrationOptionsMutation,
	useRegisterPasskeyMutation,
	useGetPasskeyAuthenticationOptionsMutation,
	useAuthenticateWithPasskeyMutation,
	useGetPasskeyCredentialsQuery,
	useDeletePasskeyCredentialMutation,
	useRenamePasskeyCredentialMutation,
	useRegisterWithPasskeyInitMutation,
	useRegisterWithPasskeyCompleteMutation,
	useGetPrivacySettingsQuery,
	useUpdatePrivacySettingsMutation,
	useUploadAvatarMutation,
	useGetNotificationSettingsQuery,
	useUpdateNotificationSettingsMutation,
} = userApi

export const {
	useGetUserAccountInfoQuery,
	useUnlinkAccountMutation,
	useRevokeDeviceMutation,
	useSetPrimaryAccountMutation,
	useGetUserSubscriptionQuery,
	useGetFullAccessOfferQuery,
	useAcceptFullAccessOfferMutation,
	useGetSupporterOfferQuery,
	useCreateCheckoutSessionMutation,
	useCreatePortalSessionMutation,
} = userAccountsApi
